import React from "react";
import { AppFilterProps, SelectOptions } from './interfaces';
import { userDispatch } from './localStorage';
import routes from '../../../Utils/routes';
import { Redirect } from 'react-router-dom';
import { useEffect, useState } from 'react';
import KenLoader from '../globalComponent/KenLoader';

export const PublicRouter: React.FunctionComponent<any> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    userDispatch({ case: 'GET', key: 'user' })
      ?.then((__user) => {
        if (__user?.email || __user?.id) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      })
      .catch((err) => {
        setIsAuthenticated(false);
      });
  }, []);

  return !isAuthenticated ? <>{children}</> : <Redirect to={routes.events} />;
};

export const PrivateRouter: React.FunctionComponent<any> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    userDispatch({ case: 'GET', key: 'role' })
      ?.then((res) => {
        if (res?.roleType === 'guest') {
          setIsAuthenticated(true);
        } else {
          userDispatch({ case: 'GET', key: 'user' })
            ?.then((__user) => {
              if (__user?.email || __user?.id) {
                setIsAuthenticated(true);
              } else {
                setIsAuthenticated(false);
              }
            })
            .catch((err) => {
              setIsAuthenticated(false);
            });
        }
      })
      .catch((err) => {
        setIsAuthenticated(false);
      });
  }, []);

  if (isAuthenticated === null) {
    return <KenLoader />;
  }

  return isAuthenticated ? <>{children}</> : <Redirect to={routes.events} />;
};

export const removeHTMLTags = (htmlString: string) => {
  return htmlString.replace(/<[^>]*>/g, '');
};

export const ApiConfigs: (accessToken: string, refreshToken: string, navigate: any) => void = (accessToken: string, refreshToken: string, navigate: any) => {
  if (accessToken) {
    // Configure API with tokens if needed
    // This can be implemented based on your API configuration needs
    console.log('API configured with tokens');
  }
};

export const addRouteHistory = async (pathname: string) => {
  // Store route history if needed
  console.log('Route history added:', pathname);
};

export const toBase64: (file: any) => Promise<unknown> = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>, files: any, types?: Array<string>, size?: number) => {
  // File Check
  if (!e.target.files) {
    return { message: 'Please Upload File', success: false };
  }
  const getFile = e.target.files[0];
  const maxSize = size ? size : 2000000; // 2MB default

  // File Type Check
  if (types) {
    const getMatch: Array<string> = types?.filter((items) => {
      return items === getFile.type;
    });
    if (getMatch.length === 0) {
      return {
        message: `Only the ${types.map((items) => {
          return `${items} `;
        })} formats are accepted`,
        success: false,
      };
    }
  }

  // File Size Check (only max size)
  if (getFile.size > maxSize) {
    return { message: 'File size must not exceed 2MB', success: false };
  }

  return { data: await toBase64(e.target.files[0]), success: true };
};

export function removeNullKeys(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => removeNullKeys(item)).filter((item) => item !== null && item !== undefined && Object.keys(item).length > 0);
  } else if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === null || obj[key] === undefined) {
        delete obj[key];
      } else if (typeof obj[key] === 'object') {
        obj[key] = removeNullKeys(obj[key]);
        if (Object.keys(obj[key]).length === 0) {
          delete obj[key];
        }
      }
    });
  }
  return obj;
}

export const splitBase64 = (base64: string) => {
  const splitData = base64.split(',');
  return splitData[1];
};

export const AppFilterFunction: (props: AppFilterProps) => Array<any> = (props) => {
  const { filterData, data } = props;
  if (Object.keys(filterData).length === 0) {
    return data;
  }

  const filteringData = { ...filterData };
  let sampleEventData = [...data];
  for (const i in filteringData) {
    const filteredArray = [];
    for (let j = 0; j < sampleEventData.length; j++) {
      const element = sampleEventData[j];
      if (element[i] !== null) {
        if (`${element[i]}`.toLowerCase().includes(`${filteringData[i]}`.toLowerCase())) {
          filteredArray.push(element);
        }
      }
    }
    sampleEventData = [...filteredArray];
  }
  return sampleEventData;
};

export const handleFilterData: (key: string, array: Array<any>) => Array<SelectOptions> = (key: string, array: Array<any>) => {
  const set = new Set();
  const handleNull = (data: string) => {
    if (data && data !== null) {
      set.add(data);
    }
  };
  for (let i = 0; i < array?.length; i++) {
    if (array[i]) {
      const category = array[i][key] as string;
      if (category && `${category}`.includes(';')) {
        const categoryArray = category?.split(';');
        for (let j = 0; j < categoryArray.length; j++) {
          handleNull(categoryArray[j]);
        }
      } else {
        handleNull(category);
      }
    }
  }
  return Array.from(set).map((items: any) => {
    return { label: items, value: items };
  }) as Array<SelectOptions>;
};

export const shrinkTextBased = (limit = 0, text = '') => {
  return text.length > limit ? `${text.substring(0, limit)}...` : text;
};

export function generateUniqueKey() {
  const nowPart = Math.floor(performance.now()).toString(36).toUpperCase();
  const randomPart = Math.random().toString(36).slice(2, 4).toUpperCase();
  return (nowPart + randomPart).padEnd(6, 'X').slice(0, 6);
}

export function addNameSuffixToObject(obj: any, suffix: string): any {
  return Object.keys(obj).reduce((acc: any, key: string) => {
    acc[`${key}-${suffix}`] = obj[key];
    return acc;
  }, {});
}

export function convertArrayToText(arr: any[]) {
  return arr.map((item) => item.value).join(';');
}

export function getUniqueObjects(A: any[], B: any[], key = 'Id') {
  return A.filter((a) => !B.some((b) => a[key] === b[key]));
}
